import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { useEffect, useMemo, useRef } from 'react';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { warehouseRequestOptions } from 'src/sections/@dashboard/warehouse/utils/request';

/**
 * The cache scope of a read made with no organization selected: an owner's,
 * whose answers carry no Warehouse data.
 */
export const OWNER_CACHE_SCOPE = 'owner';

export type ClinicalQueryKey = readonly [
  string,
  string | null | undefined,
  string,
  ...unknown[],
];

/**
 * R1-1 (#675): the key of a medication or treatment read. Its answer carries
 * the viewing clinic's Warehouse data (`stockIssues`, `materials`), and the
 * QueryClient outlives an organization switch, so the cache scope (the
 * organization, or `OWNER_CACHE_SCOPE`) follows the patient or visit id. The
 * `[prefix, id]` invalidations still reach every scope: a write changes the
 * entry that each clinic's copy describes.
 */
export function clinicalQueryKey(
  prefix: string,
  id: string | null | undefined,
  scope: string,
  ...parts: unknown[]
): ClinicalQueryKey {
  return [prefix, id, scope, ...parts];
}

export interface ClinicalCacheScope {
  /** The selected organization's id, or `OWNER_CACHE_SCOPE`. */
  scope: string;
  /**
   * The read's request options. They name the scope's organization, so a
   * switch during the token refresh cannot answer this scope's key with
   * another organization's data; `signal` cancels a superseded fetch.
   */
  requestOptions: (signal?: AbortSignal) => AxiosRequestConfig;
}

export function clinicalCacheScope(
  organizationId: string | undefined,
): ClinicalCacheScope {
  return {
    scope: organizationId || OWNER_CACHE_SCOPE,
    requestOptions: (signal) => {
      if (organizationId) {
        return warehouseRequestOptions(organizationId, signal);
      }
      return signal ? { signal } : {};
    },
  };
}

/** Stable while the selected organization is. */
export function useClinicalCacheScope(): ClinicalCacheScope {
  const organizationId = useOrganizationContext().selectedOrganization?.id;
  return useMemo(() => clinicalCacheScope(organizationId), [organizationId]);
}

export interface ClinicalQueryOptions {
  enabled?: boolean;
  retry?: number | boolean;
  /** Only within one scope: a page never stands in for another organization's. */
  keepPreviousData?: boolean;
}

/**
 * A medication or treatment read in the selected organization's cache scope
 * (see `clinicalQueryKey`). `fetcher` passes `options` to the API method.
 */
export function useClinicalQuery<T>(
  prefix: string,
  id: string | null | undefined,
  parts: readonly unknown[],
  fetcher: (options: AxiosRequestConfig) => Promise<T>,
  {
    enabled = true,
    retry,
    keepPreviousData = false,
  }: ClinicalQueryOptions = {},
): UseQueryResult<T> {
  const { scope, requestOptions } = useClinicalCacheScope();
  // The scope of the last answer this hook got for its own key (never
  // carried-over data): keepPreviousData holds only while it is still the
  // selected one, as in `useWarehouseQuery`.
  const answeredScope = useRef<string | undefined>(undefined);

  const query = useQuery({
    queryKey: clinicalQueryKey(prefix, id, scope, ...parts),
    queryFn: ({ signal }) => fetcher(requestOptions(signal)),
    enabled,
    keepPreviousData: keepPreviousData && answeredScope.current === scope,
    ...(retry !== undefined ? { retry } : {}),
  });

  const ownAnswer = query.isSuccess && !query.isPreviousData;
  useEffect(() => {
    if (ownAnswer) {
      answeredScope.current = scope;
    }
  }, [ownAnswer, scope]);

  return query;
}
